import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Trophy, Shield, Flame, Lock, Star, Sparkles, Dumbbell, Brain,
  Award, Droplets, Footprints, Moon, Sun, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const SECTIONS = [
  { id: 'all', label_fr: 'Tous', label_en: 'All', icon: Trophy, color: 'text-amber-400' },
  { id: 'carburant', label_fr: 'Le Carburant', label_en: 'Fuel', icon: Droplets, color: 'text-red-400' },
  { id: 'forge', label_fr: 'La Forge', label_en: 'The Forge', icon: Dumbbell, color: 'text-orange-400' },
  { id: 'citadelle', label_fr: 'La Citadelle', label_en: 'The Citadel', icon: Brain, color: 'text-purple-400' },
  { id: 'grades', label_fr: 'Grades Extra Miles', label_en: 'Extra Miles Ranks', icon: Award, color: 'text-blue-400' },
];

const RARITY_ORDER = { common: 0, rare: 1, epic: 2, legendary: 3 };
const RARITY_LABELS = {
  common: { fr: 'Commun', en: 'Common', color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/20' },
  rare: { fr: 'Rare', en: 'Rare', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  epic: { fr: 'Epique', en: 'Epic', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  legendary: { fr: 'Legendaire', en: 'Legendary', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
};

const SECTION_ICONS = {
  carburant: Droplets,
  forge: Dumbbell,
  citadelle: Brain,
  grades: Award,
};

function BadgeCard({ badge, earned, language }) {
  const fr = language === 'fr';
  const rarity = RARITY_LABELS[badge.rarity] || RARITY_LABELS.common;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative p-4 rounded-xl border transition-all ${
        earned
          ? 'bg-gradient-to-br from-card to-card border-primary/30 shadow-lg shadow-primary/5'
          : 'bg-card/50 border-border/50 opacity-60'
      }`}
      data-testid={`badge-card-${badge.id}`}
    >
      {/* Rarity indicator */}
      {earned && (badge.rarity === 'epic' || badge.rarity === 'legendary') && (
        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: badge.color, boxShadow: `0 0 10px ${badge.color}40` }}>
          <Sparkles className="w-3 h-3 text-black" />
        </div>
      )}

      <div className="flex items-start gap-3">
        <div
          className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 text-2xl ${
            earned ? 'shadow-lg' : 'grayscale opacity-40'
          }`}
          style={{
            backgroundColor: earned ? `${badge.color}15` : undefined,
            boxShadow: earned ? `0 0 20px ${badge.color}20` : undefined,
          }}
        >
          {earned ? (
            <span>{badge.icon}</span>
          ) : (
            <Lock className="w-6 h-6 text-muted-foreground/40" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`font-heading text-sm uppercase tracking-tight ${earned ? 'text-foreground' : 'text-muted-foreground'}`}>
            {badge.name}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {badge.description}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${rarity.bg} ${rarity.color}`}>
              {fr ? rarity.fr : rarity.en}
            </span>
            {earned && badge.earned_at && (
              <span className="text-[10px] text-muted-foreground">
                {new Date(badge.earned_at).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function BadgesPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [earned, setEarned] = useState([]);
  const [unearned, setUnearned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('all');

  const fetchBadges = useCallback(async () => {
    try {
      const [earnedRes, progressRes] = await Promise.all([
        api.get('/badges'),
        api.get('/badges/progress'),
      ]);
      setEarned(earnedRes.data.badges || []);
      setUnearned(progressRes.data.progress || []);
    } catch (err) {
      toast.error(fr ? 'Erreur chargement badges' : 'Error loading badges');
    } finally {
      setLoading(false);
    }
  }, [fr]);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  // Build combined list: earned badges first, then locked
  const allBadges = [
    ...earned.map(b => ({ ...b, earned: true })),
    ...unearned.filter(u => !earned.some(e => e.id === u.id)).map(b => ({ ...b, earned: false })),
  ];

  // Filter by section
  const filtered = activeSection === 'all'
    ? allBadges
    : allBadges.filter(b => b.section === activeSection);

  // Sort: earned first, then by rarity (legendary first)
  const sorted = [...filtered].sort((a, b) => {
    if (a.earned !== b.earned) return a.earned ? -1 : 1;
    return (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0);
  });

  const earnedCount = earned.length;
  const totalCount = earned.length + unearned.length;

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="badges-page">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-foreground">
            {fr ? 'Arsenal des Badges' : 'Badge Arsenal'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {fr
              ? `${earnedCount} / ${totalCount} badges debloques`
              : `${earnedCount} / ${totalCount} badges unlocked`}
          </p>
          <Progress
            value={totalCount > 0 ? (earnedCount / totalCount) * 100 : 0}
            className="h-2 max-w-xs mx-auto bg-secondary"
          />
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {SECTIONS.map(section => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            const sectionCount = section.id === 'all'
              ? earnedCount
              : earned.filter(b => b.section === section.id).length;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-heading uppercase tracking-tight whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/30'
                    : 'bg-secondary/50 text-muted-foreground border border-transparent hover:bg-secondary'
                }`}
                data-testid={`badge-section-${section.id}`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? section.color : ''}`} />
                {fr ? section.label_fr : section.label_en}
                {sectionCount > 0 && (
                  <span className="bg-primary/20 text-primary text-[10px] px-1.5 py-0.5 rounded-full font-black">
                    {sectionCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Section header for filtered view */}
        {activeSection !== 'all' && (
          <div className="flex items-center gap-2 pt-2">
            {(() => {
              const Icon = SECTION_ICONS[activeSection] || Trophy;
              const section = SECTIONS.find(s => s.id === activeSection);
              return (
                <>
                  <Icon className={`w-5 h-5 ${section?.color || 'text-primary'}`} />
                  <h2 className="font-heading text-xl uppercase tracking-tight text-foreground">
                    {fr ? section?.label_fr : section?.label_en}
                  </h2>
                </>
              );
            })()}
          </div>
        )}

        {/* Badges Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AnimatePresence mode="popLayout">
            {sorted.map(badge => (
              <BadgeCard
                key={badge.id}
                badge={badge}
                earned={badge.earned}
                language={language}
              />
            ))}
          </AnimatePresence>
        </div>

        {sorted.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">{fr ? 'Aucun badge dans cette section' : 'No badges in this section'}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
